import React from "react";
import PropTypes from "prop-types";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";
import CardContainer from "@src/components/cards/CardContainer";
import HorizontalLayout from "@src/components/cards/HorizontalLayout";
import { ImageWrapper } from "@src/components/atoms";
import { useDCContext } from "@src/context/dcContext";
import { useIsMobileWidth } from "@src/utils/mediaQuery";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const BehaviourInsightCard = (props) => {
  const { behaviour, eventType = "opportunity", metadata } = props;
  const color = getBehaviourColourForPosition(behaviour.order).name;
  const dc = useDCContext();
  const { getDynamicReplacement, existsDynamicReplacements } = dc;
  const isMobile = useIsMobileWidth();

  const card = (key, title, statement) => {
    return (
      <div key={key} className="px-2 md:px-4 pb-2 md:pb-4">
        <div className={`h-full bg-${color}-100 rounded-xl`}>
          <div className="py-2">
            <div className="">{statement}</div>
          </div>
        </div>
      </div>
    );
  };
  const getInsightList = () => {
    // to calculate what to show only once
    const bvr = behaviour;
    let insights = [];
    let insightsCount = 1;
    const insightsLimit = 1;
    bvr?.insightsV2?.sort(() => Math.random() - 0.5); // shuffle insights
    bvr?.insightsV2?.forEach((insight) => {
      //"Social Proof" insight (eg you did better than 80%, or X was performed only 20 times at Y)
      if (insightsCount <= insightsLimit) {
        let msgInsight = insight.insightStatement;
        if (insight.messageType && insight.values) {
          // NEW insights format
          const isSuccess = bvr.isSuccess;
          let insightType = "rate";
          if (insight.values["LAST_N_DAYS"]) {
            if (isSuccess) {
              insightType =
                insight.values["RATE_THRESHOLD"] > 50 ? "count" : "rate";
            } else {
              insightType =
                insight.values["RATE_THRESHOLD"] < 50 ? "count" : "rate";
            }

            if (
              insightType === "rate" &&
              insight.values["RATE_THRESHOLD"] >= 40 &&
              insight.values["RATE_THRESHOLD"] <= 70
            ) {
              return; // hide insight when rate is between 40-70%
            }
          }
          const insightReplacement =
            `captain__events__behaviour__insights__${insight.values?.DIMENSION}__${insight.messageType}_${insightType}`.toLowerCase();
          const insightValues = {};
          Object.keys(insight.values).forEach((key) => {
            const val = insight.values[key];
            if (isNaN(val)) {
              insightValues[key] = String(val).replaceAll("_", " ");
            } else {
              insightValues[key] = Number(val).toFixed(0);
            }
          });
          msgInsight = getDynamicReplacement(
            insightReplacement,
            {
              ...insightValues,
              BEHAVIOUR_NAME: bvr.behaviourAcronym,
              EVENT_NAME: eventType.toLowerCase(),
            },
            msgInsight,
            true,
          );
        }
        if (msgInsight) {
          insights.push(
            card(
              `${bvr.id}:${insightsCount}:insight`,
              `Insight ${insightsCount}`,
              msgInsight,
            ),
          );
          insightsCount++;
        }
      }
    });
    if (bvr.behaviourAcronym === "ODFL") {
      //2024 insight format (the "Did you know?" one)
      const df_needed = Number(metadata["discretionary_fuel_needed"]);
      const df_loaded = Number(metadata["discretionary_fuel_loaded"]);
      const df_buffer =
        Number(metadata["constant_df_buffer"]) ||
        Number(metadata["constant_odfl_buffer"]);
      const isSuccess = bvr.isSuccess;
      /** "Special" ODFL success when lots of DF was loaded, and it was needed */
      const specialSuccess = isSuccess && df_loaded > df_buffer;

      /**
       * For ODFL, Display the "Did you Know" insight when:
       * 1. Failed
       * 2. Special Success
       * 3. df_loaded is zero (this is done later)
       */
      const useInsightDidYouKnow = !isSuccess || specialSuccess;
      if (useInsightDidYouKnow) {
        const dcKey = `captain__events__behaviour__insights__odfl__${isSuccess ? "success" : "failure"}`;
        if (existsDynamicReplacements(dcKey)) {
          const msgInsight = getDynamicReplacement(dcKey, {
            DF_NEEDED: AppUtils.roundTo2DP(df_needed),
            DF_LOADED: AppUtils.roundTo2DP(df_loaded),
            DF_THRESHOLD: df_buffer,
          });
          insights = [];
          insights.push(
            card(
              `${bvr.id}:${insightsCount}:insight`,
              `Insight ${insightsCount}`,
              msgInsight,
            ),
          );
        }
      }
      if (df_loaded === 0) {
        //No DF loaded
        const dcKey =
          "captain__events__behaviour__insights__odfl__no_fuel_loaded";
        if (existsDynamicReplacements(dcKey)) {
          const msgInsight = getDynamicReplacement(dcKey);
          insights = [];
          insights.push(
            card(
              `${bvr.id}:${insightsCount}:insight`,
              `Insight ${insightsCount}`,
              msgInsight,
            ),
          );
        }
      }
    }
    return insights;
  };

  const relevantInsights = getInsightList();

  return relevantInsights.length > 0 ? (
    <CardContainer
      bgColor={`${color}-100`}
      padding={isMobile ? 2 : 4}
      shadow={false}
    >
      <HorizontalLayout
        ratio={[30, "flex-1"]}
        gap={isMobile ? 2 : 4}
        className="h-full"
      >
        {/* child 1  - Savings icon*/}
        <div className="h-full flex flex-col items-center justify-center">
          <ImageWrapper
            src={
              "https://signol-email-images.s3.eu-west-2.amazonaws.com/lightbulb_icon.png"
            }
            size={isMobile ? "sm" : "md"}
          />
        </div>

        {/* child 2 - Statement with number*/}
        <div
          className={`h-full m-auto flex flex-col justify-center items-center ${isMobile ? "w-full" : "w-4/5"}`}
        >
          <div className="w-full pl-4 pt-2 font-bold text-lg">
            Did you know?
          </div>
          {getInsightList()}
        </div>
      </HorizontalLayout>
    </CardContainer>
  ) : null;
};

export const behaviourInsightCardControls = {
  behaviour: {
    propType: PropTypes.object,
    description: "Behaviour data object",
    controlType: "object",
  },
  industry: COMMON_CONTROLS.industry,
  metadata: {
    propType: PropTypes.object,
    description: "Event metadata object",
    controlType: "object",
  },
  eventType: {
    propType: PropTypes.string,
    description: "Event type",
    controlType: "text",
  },
  getDynamicReplacement: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
};

BehaviourInsightCard.propTypes = AppUtils.getPropTypeFromControls(
  behaviourInsightCardControls,
);

export default BehaviourInsightCard;
